import { acceptHMRUpdate, defineStore } from 'pinia'
import { toRaw } from 'vue'
import { useMaps } from './maps'
import { useTasksStore } from './tasks'
import {
    getPlanningProgress,
    getPathPlannigRoute,
    postPathPlanning,
    getPathPlanningOptions,
    getPlanningPresetsByTaskType
} from '../scripts/fieldsPlanning'
import { getPlan, getFieldPlans } from '../scripts/task'
import { rainbowColors } from '../helpers/rainbowColors'
import { createPlan } from '../scripts/task'
import { useFieldsConnectionStore } from './fieldsConnection'
import { useUserRole } from './userRole'
import { useTasksFilterStore } from './tasksFilter'
import i18n from '@/i18n'

export const usePlanner = defineStore('planner', {
    state: () => {
        return {
            rainbowColors: rainbowColors,
            rainbowPolylines: [],
            markers: [],
            selectedPlan: null,
            fieldPlanForRoutePlanner: null,
            selectedFieldPlanId: null,
            showFieldPlanSelection: false,
            fieldPlans: [],
            selectionMarkers: [],
            routeData: {
                field_id: null,
                task_type: '',
                starting_point: {
                    type: "FeatureCollection",
                    features: [
                        {
                            type: "Feature",
                            properties: {},
                            geometry: {
                                type: "Point",
                                coordinates: [0, 0]
                            }
                        }
                    ]
                },
                finish_point: {
                    type: "FeatureCollection",
                    features: [
                        {
                            type: "Feature",
                            properties: {},
                            geometry: {
                                type: "Point",
                                coordinates: [0, 0]
                            }
                        }
                    ]
                },
            },
            baseStart: { lat: null, lng: null }, // used for keeping the start point when clearing markers
            baseFinish: { lat: null, lng: null }, // used for keeping the finish point when clearing markers
            loading: {
                percent: -1,
                stage: '',
            },
            planningCancelled: false,
            headlandTarget: null,
            useAutoHeadlandTarget: true,
            fullyCircleCutouts: true,
            plannedRoute: null,
            plannedRoutes: [],
            currentPlannedRouteIndex: 0,
            planName: '',
            loadingSave: false,
            saveError: null,
            intervalID: null,
            intervalIDs: {},
            taskTypes: [],
            uncoveredPolygons: [],
            updatingPlan: false,
            updatePathPolylines: [],
            speedInterval: 0,
            allowDirectionChange: false,
            planningError: '',
            retrySeconds: null,
            mapClickListener: null,
            corridorErrorCost: {},
            headlandCostExponent: {},
            missedPathPenalty: {},
            pathPlanningOptions: [],
            useSameStartFinishPointsByPreset: {},
            useSameAllowDirectionChangeByPreset: {},
            presetAllowDirectionChangeByPreset: {},
            useSameTurnHeadlandByPreset: {},
            presetUseAutoHeadlandTargetByPreset: {},
            presetHeadlandTargetByPreset: {},
            presetBackendHeadlandIndexByPreset: {},
            presetStartPointByPreset: {},
            presetFinishPointByPreset: {},
            presetPointMarkersByPreset: {},
            presetPointPicking: {
                option: null, // e.g. 'variant_1'
                point: null,  // 'start' | 'finish'
            },
            getPlanLoading: false,
            showAdvancedOptions: false,
            uncoveredZonesPulsing: false,
            uncoveredPulsingInterval: null,
            makeAutonomousRoute: false,
        }
    },
    actions: {
        // Initialize default values for a preset
        initPresetDefaults(presetKey, taskType) {
            this.useSameStartFinishPointsByPreset[presetKey] = true
            this.useSameAllowDirectionChangeByPreset[presetKey] = true
            this.useSameTurnHeadlandByPreset[presetKey] = true
            this.presetAllowDirectionChangeByPreset[presetKey] = this.allowDirectionChange
            this.presetUseAutoHeadlandTargetByPreset[presetKey] = this.useAutoHeadlandTarget
            this.presetHeadlandTargetByPreset[presetKey] = this.headlandTarget

            this.presetStartPointByPreset[presetKey] = { lat: null, lng: null }
            this.presetFinishPointByPreset[presetKey] = { lat: null, lng: null }
        },
        buildPointFeatureCollection(lng, lat) {
            return {
                type: 'FeatureCollection',
                features: [
                    {
                        type: 'Feature',
                        properties: {},
                        geometry: {
                            type: 'Point',
                            coordinates: [lng, lat]
                        }
                    }
                ]
            }
        },
        startPresetPointPicking(option, point) {
            if (!option || (point !== 'start' && point !== 'finish')) {
                return
            }

            this.useSameStartFinishPointsByPreset[option] = false
            this.presetPointPicking = { option, point }
        },
        cancelPresetPointPicking() {
            this.presetPointPicking = { option: null, point: null }
        },
        handlePickedPresetPoint(option, point, lat, lng) {
            if (!option || (point !== 'start' && point !== 'finish')) {
                return
            }
            if (typeof lat !== 'number' || typeof lng !== 'number') {
                return
            }
            this.useSameStartFinishPointsByPreset[option] = false

            if (point === 'start') {
                this.presetStartPointByPreset[option] = { lat, lng }
            } else {
                this.presetFinishPointByPreset[option] = { lat, lng }
            }
            this.upsertPresetPointMarker(option, point, lat, lng)
        },
        clearPresetPointMarker(option, point) {
            const marker = this.presetPointMarkersByPreset?.[option]?.[point]
            if (marker) {
                toRaw(marker).map = null
            }
            if (this.presetPointMarkersByPreset?.[option]) {
                this.presetPointMarkersByPreset[option][point] = null
            }
        },
        clearPresetPointMarkersForOption(option) {
            if (!option) return
            this.clearPresetPointMarker(option, 'start')
            this.clearPresetPointMarker(option, 'finish')
        },
        clearPresetPointMarkers() {
            Object.keys(this.presetPointMarkersByPreset).forEach((option) => {
                this.clearPresetPointMarkersForOption(option)
            })
            this.presetPointMarkersByPreset = {}
        },
        resetPresetPointCoordinates() {
            Object.keys(this.useSameStartFinishPointsByPreset).forEach((option) => {
                this.useSameStartFinishPointsByPreset[option] = true
                this.presetStartPointByPreset[option] = { lat: null, lng: null }
                this.presetFinishPointByPreset[option] = { lat: null, lng: null }
            })
            this.cancelPresetPointPicking()
        },
        upsertPresetPointMarker(option, point, lat, lng) {
            if (!option || (point !== 'start' && point !== 'finish')) {
                return
            }
            if (typeof lat !== 'number' || typeof lng !== 'number' || !Number.isFinite(lat) || !Number.isFinite(lng)) {
                this.clearPresetPointMarker(option, point)
                return
            }

            this.clearPresetPointMarker(option, point)
            const mapStore = useMaps()
            const color = point === 'start' ? '#22c55e' : '#ef4444'

            const markerEl = document.createElement('div')
            markerEl.style.width = '28px'
            markerEl.style.height = '28px'
            markerEl.style.borderRadius = '50%'
            markerEl.style.background = color
            markerEl.style.border = '2px solid #111827'
            markerEl.style.display = 'flex'
            markerEl.style.alignItems = 'center'
            markerEl.style.justifyContent = 'center'
            markerEl.style.boxShadow = '0 1px 4px rgba(0,0,0,0.35)'
            markerEl.style.color = '#ffffff'

            const icon = document.createElement('i')
            icon.className = this.getIcon(option)
            icon.style.fontSize = '12px'
            markerEl.appendChild(icon)

            // find option value and use the label
            const optionText = this.getPlanningPresetLabel(option)

            const marker = new mapStore.google.maps.marker.AdvancedMarkerElement({
                position: { lat, lng },
                map: toRaw(mapStore.map),
                title: `${optionText} ${i18n.global.t(point)}`,
                content: markerEl,
                gmpClickable: false
            })

            if (!this.presetPointMarkersByPreset[option]) {
                this.presetPointMarkersByPreset[option] = { start: null, finish: null }
            }
            this.presetPointMarkersByPreset[option][point] = marker
        },
        syncPresetPointMarkers(option) {
            if (this.useSameStartFinishPointsByPreset[option]) {
                this.clearPresetPointMarkersForOption(option)
                return
            }

            const sp = this.presetStartPointByPreset[option]
            const fp = this.presetFinishPointByPreset[option]
            this.upsertPresetPointMarker(option, 'start', sp?.lat, sp?.lng)
            this.upsertPresetPointMarker(option, 'finish', fp?.lat, fp?.lng)
        },
        showPresetMarkersForOption(activeOption) {
            // Show markers only for the active planning preset (used when navigating variants)
            Object.keys(this.presetPointMarkersByPreset || {}).forEach((option) => {
                if (option !== activeOption) {
                    this.clearPresetPointMarkersForOption(option)
                }
            })

            if (!activeOption) {
                return
            }
            this.syncPresetPointMarkers(activeOption)
        },
        // Getter
        startPoint() {
            return {
                lat: this.routeData.starting_point.features[0].geometry.coordinates[1],
                lng: this.routeData.starting_point.features[0].geometry.coordinates[0]
            }
        },
        // Setter
        setStartPointLat(value) {
            this.routeData.starting_point.features[0].geometry.coordinates[1] = value
        },
        // Setter
        setStartPointLng(value) {
            this.routeData.starting_point.features[0].geometry.coordinates[0] = value
        },
        setGlobalStartPoint(lat, lng) {
            this.routeData.starting_point.features[0].geometry.coordinates = [lng, lat]
            if (this.baseStart.lat === null || this.baseStart.lng === null) {
                this.baseStart = { lat, lng }
            }
        },

        // Getter
        finishPoint() {
            return {
                lat: this.routeData.finish_point.features[0].geometry.coordinates[1],
                lng: this.routeData.finish_point.features[0].geometry.coordinates[0]
            }
        },
        // Setter
        setFinishPointLat(value) {
            this.routeData.finish_point.features[0].geometry.coordinates[1] = value
        },
        // Setter
        setFinishPointLng(value) {
            this.routeData.finish_point.features[0].geometry.coordinates[0] = value
        },
        setGlobalFinishPoint(lat, lng) {
            this.routeData.finish_point.features[0].geometry.coordinates = [lng, lat]
            if (this.baseFinish.lat === null || this.baseFinish.lng === null) {
                this.baseFinish = { lat, lng }
            }
        },
        addListener() {
            const mapStore = useMaps()
            this.removeListener()
            /**
             * Need this listener for choosing start and finish 
             * points on the map, other than those of AB Lines
             */
            this.mapClickListener = mapStore.google.maps.event.addListener(mapStore.map, 'click', (event) => {
                const lat = event.latLng.lat()
                const lng = event.latLng.lng()

                // Preset-point picking has priority over global start/finish selection
                if (this.presetPointPicking?.option && this.presetPointPicking?.point) {
                    const { option, point } = this.presetPointPicking
                    this.handlePickedPresetPoint(option, point, lat, lng)
                    this.cancelPresetPointPicking()
                    return
                }

                const startPoint = this.startPoint()
                const finishPoint = this.finishPoint()

                // Don't need to set global start/finish points on map if updating plan (only per variant)
                if (!startPoint.lng && !startPoint.lat && !this.updatingPlan) {
                    this.setGlobalStartPoint(lat, lng)
                    this.addStartMarker({ lat, lng }, 'Start')
                } else if (!finishPoint.lng && !finishPoint.lat && !this.updatingPlan) {
                    this.setGlobalFinishPoint(lat, lng)
                    this.addFinishMarker({ lat, lng }, 'Finish')
                }
            })
        },
        setFieldClickable(value) {
            const tasksStore = useTasksStore()
            const mapStore = useMaps()

            if (tasksStore.field) {
                // Use the polygon from mapStore.fields
                const mapField = mapStore.fields.find(f => f.id === tasksStore.field.id)

                if (mapField && mapField.poly) {
                    toRaw(mapField.poly).setOptions({
                        clickable: value
                    })
                }
            }
        },
        removeListener() {
            const mapStore = useMaps()

            if (this.mapClickListener) {
                mapStore.google.maps.event.removeListener(this.mapClickListener)
                this.mapClickListener = null
            }
        },
        createMarker(position, text, scale, background, title) {
            const mapStore = useMaps()

            const pinElement = new mapStore.google.maps.marker.PinElement({
                background: background,
                borderColor: '#000000',
                glyphColor: '#FFFFFF',
                scale: scale,
                glyphText: text,
            })

            const marker = new mapStore.google.maps.marker.AdvancedMarkerElement({
                position: position,
                map: toRaw(mapStore.map),
                title: title,
                content: pinElement,
                gmpClickable: true
            })

            marker.position = position

            marker.element.addEventListener('mouseenter', () => {
                pinElement.style.transform = 'scale(1.5)'
                marker.zIndex = 1000
                marker.title = this.getMarkerTitle()
            })

            marker.element.addEventListener('mouseleave', () => {
                pinElement.style.transform = 'scale(1)'
                marker.zIndex = 0
            })

            marker.addEventListener('gmp-click', () => {
                const startPoint = this.startPoint()
                const finishPoint = this.finishPoint()
                const markerCoords = this.extractLatLngFromPosition(marker.position)
                const isPresetPickingActive = this.presetPointPicking?.option && this.presetPointPicking?.point

                // Keep start/finish markers read-only outside planning/update selection mode.
                if (!this.mapClickListener && !isPresetPickingActive) {
                    return
                }

                // Preset-point picking has priority over global start/finish selection
                if (isPresetPickingActive) {
                    const { option, point } = this.presetPointPicking
                    this.handlePickedPresetPoint(option, point, markerCoords.lat, markerCoords.lng)
                    this.cancelPresetPointPicking()
                    return
                }

                // Global start/finish can be created here because is the start and the end of the path (from backend)
                if (!startPoint.lng && !startPoint.lat) {
                    this.hideSelectionMarker(marker)
                    this.setGlobalStartPoint(markerCoords.lat, markerCoords.lng)
                    this.addStartMarker({ lat: markerCoords.lat, lng: markerCoords.lng }, text)
                } else if (!finishPoint.lng && !finishPoint.lat) {
                    this.hideSelectionMarker(marker)
                    this.setGlobalFinishPoint(markerCoords.lat, markerCoords.lng)
                    this.addFinishMarker({ lat: markerCoords.lat, lng: markerCoords.lng }, text)
                }
            })

            return marker
        },
        extractLatLngFromPosition(position) {
            const rawLat = position?.lat
            const rawLng = position?.lng

            const lat = typeof rawLat === 'function' ? rawLat.call(position) : rawLat
            const lng = typeof rawLng === 'function' ? rawLng.call(position) : rawLng

            return { lat, lng }
        },
        hideSelectionMarker(marker) {
            const markerIndex = this.selectionMarkers.indexOf(marker)
            if (markerIndex !== -1) {
                this.selectionMarkers[markerIndex].map = null
                this.selectionMarkers.splice(markerIndex, 1)
            }
        },
        ckeckValidRouteRequest() {
            if (this.routeData.task_type && this.planName && this.planName.trim()) {
                return true
            }
            return false
        },
        clearRainbowPolylines() {
            this.rainbowPolylines.forEach((polyline) => {
                toRaw(polyline).setMap(null)
            })
            this.rainbowPolylines = []
        },
        clearUpdatePathPolylines() {
            // Clear update path polylines
            this.updatePathPolylines.forEach((polyline) => {
                if (polyline) {
                    const rawPolyline = toRaw(polyline)
                    // Stop animation to prevent memory leaks
                    if (rawPolyline.animationInterval) {
                        clearInterval(rawPolyline.animationInterval)
                        rawPolyline.animationInterval = null
                    }
                    rawPolyline.setMap(null)
                }
            })
            this.updatePathPolylines = []
        },
        clearMarkers(addSelectionMarkers = true, clearPlannedResults = true, preserveRainbowWhenUpdating = false, preserveStartFinishMarkers = false) {
            // decide if we need to keep start and finish markers
            this.preserveOrClearStartFinishMarkers(preserveStartFinishMarkers)

            if (addSelectionMarkers) {
                this.routeData.starting_point.features[0].geometry.coordinates = [0, 0]
                this.routeData.finish_point.features[0].geometry.coordinates = [0, 0]
                this.baseStart = { lat: null, lng: null }
                this.baseFinish = { lat: null, lng: null }
                this.addSelectionMarkers()
                this.clearPresetPointMarkers()
                this.resetPresetPointCoordinates()
            }

            // In update mode, the "old path" is represented by rainbowPolylines (styled black)
            // if not preserving rainbow when updating, clear it
            if (!preserveRainbowWhenUpdating) {
                this.clearRainbowPolylines()
                this.updatingPlan = false
                this.selectedFieldPlanId = null
                this.fieldPlanForRoutePlanner = null
            }
            this.clearUpdatePathPolylines()
            this.clearPaths()
            if (clearPlannedResults) {
                this.plannedRoute = null
                this.plannedRoutes = []
                this.currentPlannedRouteIndex = 0
            }
            this.loading = { percent: -1, stage: '' }
            this.clearUncoveredPolygons()
        },
        preserveOrClearStartFinishMarkers(preserveStartFinishMarkers) {
            if (preserveStartFinishMarkers) {
                this.markers.forEach((marker) => {
                    marker.map = null
                })
                this.markers = []
                Object.keys(this.useSameStartFinishPointsByPreset).forEach((option) => {
                    this.syncPresetPointMarkers(option)
                })

                const hasBaseStart = this.baseStart.lat !== null && this.baseStart.lng !== null
                const hasBaseFinish = this.baseFinish.lat !== null && this.baseFinish.lng !== null

                if (hasBaseStart) {
                    this.setGlobalStartPoint(this.baseStart.lat, this.baseStart.lng)
                    this.addStartMarker({ lat: this.baseStart.lat, lng: this.baseStart.lng }, 'Start')
                }
                if (hasBaseFinish) {
                    this.setGlobalFinishPoint(this.baseFinish.lat, this.baseFinish.lng)
                    this.addFinishMarker({ lat: this.baseFinish.lat, lng: this.baseFinish.lng }, 'Finish')
                }

                if (!hasBaseStart || !hasBaseFinish) {
                    this.addSelectionMarkers()
                }
            } else {
                this.markers.forEach((marker) => {
                    marker.map = null
                })
                this.markers = []
                this.clearPresetPointMarkers()
            }
        },
        clearUncoveredPolygons() {
            this.stopUncoveredPulsing()
            this.uncoveredPolygons.forEach(polygon => {
                if (polygon && polygon.setMap) {
                    toRaw(polygon).setMap(null)
                }
            })
            this.uncoveredPolygons = []
        },
        onLeaveFieldPaths() {
            this.clearMarkers(false)
            this.clearRainbowPolylines()
            this.selectedPlan = null
            this.clearRouteData()
            this.planName = ''
            this.clearUncoveredPolygons()
            this.uncoveredZonesPulsing = false
            this.stopUncoveredPulsing()
        },
        clearRouteData() {
            this.routeData = {
                field_id: null,
                task_type: '',
                starting_point: {
                    type: "FeatureCollection",
                    features: [
                        {
                            type: "Feature",
                            properties: {},
                            geometry: {
                                type: "Point",
                                coordinates: [0, 0]
                            }
                        }
                    ]
                },
                finish_point: {
                    type: "FeatureCollection",
                    features: [
                        {
                            type: "Feature",
                            properties: {},
                            geometry: {
                                type: "Point",
                                coordinates: [0, 0]
                            }
                        }
                    ]
                },
            }
            this.baseStart = { lat: null, lng: null }
            this.baseFinish = { lat: null, lng: null }
        },
        onLeaveRoutePlanner() {
            const mapStore = useMaps()

            this.stopUncoveredPulsing()
            this.uncoveredZonesPulsing = false
            // Full cleanup without re-adding selection markers on leave
            this.clearMarkers(false)
            this.clearSelectionMarkers()
            this.removeListener()
            this.cancelPresetPointPicking()

            this.clearRouteData()
            this.planName = ''
            this.headlandTarget = null
            this.useAutoHeadlandTarget = true
            this.fullyCircleCutouts = true
            this.makeAutonomousRoute = false
            this.updatingPlan = false
            this.allowDirectionChange = false
            this.fieldPlanForRoutePlanner = null
            this.selectedFieldPlanId = null
            this.showFieldPlanSelection = false
            this.showAdvancedOptions = false

            // Clear per-preset start/finish state
            this.useSameStartFinishPointsByPreset = {}
            this.useSameAllowDirectionChangeByPreset = {}
            this.presetAllowDirectionChangeByPreset = {}
            this.useSameTurnHeadlandByPreset = {}
            this.presetUseAutoHeadlandTargetByPreset = {}
            this.presetHeadlandTargetByPreset = {}
            this.presetBackendHeadlandIndexByPreset = {}
            this.presetStartPointByPreset = {}
            this.presetFinishPointByPreset = {}
            this.presetPointMarkersByPreset = {}

            // Undo drawPath's map.data.setStyle({ clickable: false }) so shared data styling resets
            mapStore.map.data.setStyle(null)
        },
        /**
         * @param {boolean} userCancelled - If true, sets planningCancelled (e.g. user dismissed planning); pass false on API errors so RoutePlanner can still show a toast
         * @param {boolean} addSelectionMarkers - Passed to clearMarkers: when false, does not re-add blue start/finish hint markers (e.g. cancel from Update path)
         */
        cancelLoading(userCancelled = true, addSelectionMarkers = true) {
            this.loading.percent = -1
            this.loading.stage = ''
            if (userCancelled) {
                this.planningCancelled = true
            }
            // this.clearMarkers(addSelectionMarkers)
            this.clearMarkers(false, true, false, true) // this will keep start and finish markers
            this.clearRainbowPolylines()
            this.loadingSave = false
            this.updatingPlan = false
            if (this.intervalID) {
                clearInterval(this.intervalID)
                this.intervalID = null
            }
            Object.values(this.intervalIDs).forEach((intervalId) => {
                clearInterval(intervalId)
            })
            this.intervalIDs = {}
        },
        addStartMarker(position, text) {
            const marker = this.createMarker(position, text, 1, '#00FF00', 'Start')
            this.markers.push(marker)
        },
        addFinishMarker(position, text) {
            const marker = this.createMarker(position, text, 1.2, '#FF0000', 'Finish')
            this.markers.push(marker)

            // Remove the blue markers after choosing both points
            this.clearSelectionMarkers()
        },
        createPath(coordinates) {
            // First of all, be sure to create rainbow polylines if they don't exist
            if (this.rainbowPolylines.length === 0) {
                this.rainbowPolylines = this.createRainbowPolyline(coordinates)

                if (coordinates.length > 0) {
                    this.addStartMarker(coordinates[0], 'Start')
                    this.addFinishMarker(coordinates[coordinates.length - 1], 'Finish')
                }

                this.addDirectionalArrows(
                    this.rainbowPolylines,
                    '#000000', // fillColor
                    '#FFFFFF'  // strokeColor
                )
            } else if (this.updatingPlan) {
                // Save new path in separate variable when updating
                this.updatePathPolylines = this.createRainbowPolyline(coordinates)

                if (coordinates.length > 0) {
                    this.addStartMarker(coordinates[0], 'Start')
                    this.addFinishMarker(coordinates[coordinates.length - 1], 'Finish')
                }

                this.addDirectionalArrows(
                    this.updatePathPolylines,
                    '#000000', // fillColor
                    '#FFFFFF'  // strokeColor
                )

                // Animate arrows only for black path (only if speedInterval > 0)
                if (this.speedInterval > 0) {
                    const fc = useFieldsConnectionStore()
                    this.updatePathPolylines.forEach(polyline => {
                        if (polyline) {
                            // Convert speedInterval to animation interval: higher speedInterval = faster animation (lower interval)
                            // speedInterval 0 = no animation, speedInterval 100 = very fast (interval 1)
                            const animationInterval = Math.max(1, 101 - this.speedInterval)
                            fc.animateCircle(polyline, animationInterval)
                        }
                    })
                }

                // Transform existing rainbowPolylines to black color, or create them in black if they don't exist
                if (this.rainbowPolylines && this.rainbowPolylines.length > 0) {
                    // Transform existing rainbowPolylines to black
                    this.rainbowPolylines.forEach(polyline => {
                        if (polyline) {
                            toRaw(polyline).setOptions({
                                strokeColor: '#000000',
                                strokeWeight: 10,
                                zIndex: 5
                            })
                        }
                    })
                } else {
                    // Create rainbowPolylines directly in black color when accessing direct link
                    this.rainbowPolylines = this.createRainbowPolyline(coordinates, '#000000', 5, 20)

                    if (coordinates.length > 0) {
                        this.addStartMarker(coordinates[0], 'Start')
                        this.addFinishMarker(coordinates[coordinates.length - 1], 'Finish')
                    }

                    this.addDirectionalArrows(
                        this.rainbowPolylines,
                        '#000000', // fillColor
                        '#FFFFFF'  // strokeColor
                    )
                }
            } else {
                // FOR EDIT PATH MODE
                // If rainbow polylines exist (was not generate a segment/full path), restore them to original opacity and colors
                if (this.rainbowPolylines && this.rainbowPolylines.length > 0) {
                    // Restore opacity to original (1.0)
                    this.rainbowPolylines.forEach(polyline => {
                        if (polyline) {
                            toRaw(polyline).setOptions({
                                strokeOpacity: 1.0,
                                visible: true
                            })
                        }
                    })

                    // Restore rainbow colors
                    this.restoreRainbowColors()
                } else {
                    // Create rainbowPolylines if they don't exist (was generate a segment/full path)
                    this.rainbowPolylines = this.createRainbowPolyline(coordinates)

                    if (coordinates.length > 0) {
                        this.addStartMarker(coordinates[0], 'Start')
                        this.addFinishMarker(coordinates[coordinates.length - 1], 'Finish')
                    }

                    this.addDirectionalArrows(
                        this.rainbowPolylines,
                        '#000000', // fillColor
                        '#FFFFFF'  // strokeColor
                    )
                }
            }
        },
        async showPath(planId) {
            this.getPlanLoading = true
            // Clear new path polylines when entering PathDetails
            this.clearUpdatePathPolylines()
            // Reset updatingPlan when entering PathDetails (unless already in update mode)
            if (!this.updatingPlan) {
                this.updatingPlan = false
                // Restore rainbow colors if rainbowPolylines exist
                this.restoreRainbowColors()
            }

            this.clearMarkers(false)

            try {
                const res = await getPlan(planId)

                if (res && res.geometry) {
                    const geoJsonData = {
                        type: 'FeatureCollection',
                        features: [
                            {
                                type: 'Feature',
                                geometry: res.geometry,
                                properties: {
                                    name: res.name,
                                    valid_from: res.valid_from,
                                    valid_to: res.valid_to,
                                    driven: res.driven,
                                    survey_id: res.survey_id,
                                    parent_id: res.parent_id,
                                    task_id: res.task_id,
                                }
                            }
                        ]
                    }
                    this.drawPath(geoJsonData)
                }
                this.getPlanLoading = false
                return res
            } catch (error) {
                console.error('Error fetching plan data:', error)
                this.getPlanLoading = false
                return null
            }
        },
        drawPath(geoJsonData) {
            const mapStore = useMaps()

            if (this.updatingPlan) {
                // When updating, extract coordinates directly from GeoJSON and create new path
                if (geoJsonData.features && geoJsonData.features.length > 0) {
                    const geometry = geoJsonData.features[0].geometry
                    if (geometry.type === 'MultiLineString' && geometry.coordinates) {
                        // Convert coordinates from [lng, lat] to { lat, lng } format for createPath
                        // Combine all LineString segments into a single array
                        const allCoordinates = geometry.coordinates
                            .flat()  // Flatten all segments into one array
                            .map(coord => ({
                                lat: coord[1],
                                lng: coord[0]
                            }))
                        this.createPath(allCoordinates)
                    }
                }
            } else {
                // Normal flow: add GeoJSON and style it
                mapStore.map.data.addGeoJson(geoJsonData)
                let styled = false
                mapStore.map.data.setStyle((feature) => {
                    if (!styled) {
                        const geometryType = feature.getGeometry().getType()

                        if (geometryType === 'MultiLineString') {
                            // convert all coordinates to a single array
                            const allCoordinates = feature.getGeometry()
                                .getArray()                 // [LineString, LineString, ...]
                                .flatMap(lineString => lineString.getArray()) // [LatLng, LatLng, LatLng, ...]

                            this.createPath(allCoordinates)
                        }
                        styled = true
                    }
                    // Set all features to not clickable
                    return { clickable: false }
                })
            }
        },
        setNotVisible() {
            if (this.updatingPlan) {
                // Hide only new path when updating (black path stays visible)
                this.updatePathPolylines.forEach(polyline => {
                    if (polyline) {
                        toRaw(polyline).setVisible(false)
                    }
                })
            } else {
                // Hide the rainbow polylines
                this.rainbowPolylines.forEach(polyline => {
                    if (polyline) {
                        toRaw(polyline).setVisible(false)
                    }
                })

                const mapStore = useMaps()
                // Also clear the rainbow polylines coordinates from the map
                mapStore.map.data.forEach(feature => {
                    if (feature.getGeometry().getType() === 'MultiLineString') {
                        mapStore.map.data.remove(feature)
                    }
                })
            }
        },
        setVisible() {
            if (this.updatingPlan) {
                // Show new path when updating
                this.updatePathPolylines.forEach(polyline => {
                    if (polyline) {
                        toRaw(polyline).setVisible(true)
                    }
                })
            } else {
                this.rainbowPolylines.forEach(polyline => {
                    if (polyline) {
                        toRaw(polyline).setVisible(true)
                    }
                })
            }
        },
        showOldPath() {
            this.rainbowPolylines.forEach(polyline => {
                if (polyline) {
                    toRaw(polyline).setVisible(true)
                }
            })

        },
        hideOldPath() {
            this.rainbowPolylines.forEach(polyline => {
                if (polyline) {
                    toRaw(polyline).setVisible(false)
                }
            })

            // Also hide features from map.data (added via addGeoJson)
            const mapStore = useMaps()
            mapStore.map.data.forEach(feature => {
                if (feature && feature.getGeometry() && feature.getGeometry().getType() === 'MultiLineString') {
                    mapStore.map.data.overrideStyle(feature, {
                        visible: false
                    })
                }
            })
        },
        updateAnimationSpeed() {
            // Update animation speed when speedInterval changes
            if (this.updatingPlan && this.updatePathPolylines.length > 0) {
                const fc = useFieldsConnectionStore()

                // Stop existing animations
                this.updatePathPolylines.forEach(polyline => {
                    if (polyline) {
                        const rawPolyline = toRaw(polyline)
                        if (rawPolyline.animationInterval) {
                            clearInterval(rawPolyline.animationInterval)
                            rawPolyline.animationInterval = null
                        }
                    }
                })

                // Start new animations with new speed (only if speedInterval > 0)
                if (this.speedInterval > 0) {
                    this.updatePathPolylines.forEach(polyline => {
                        if (polyline) {
                            // Convert speedInterval to animation interval: higher speedInterval = faster animation (lower interval)
                            // speedInterval 0 = no animation, speedInterval 100 = very fast (interval 1)
                            const animationInterval = Math.max(1, 101 - this.speedInterval)
                            fc.animateCircle(polyline, animationInterval)
                        }
                    })
                }
            }
        },
        async startPlanning(routeDataToSend) {
            try {
                const planningResponse = await postPathPlanning(routeDataToSend)
                const planningVariants = this.normalizePlanningVariants(planningResponse)

                if (!planningResponse || planningResponse.status >= 400 || planningVariants.length === 0) {
                    this.createPlanningError(planningResponse)
                    this.cancelLoading(false)
                    return null
                }

                return planningVariants
            } catch (error) {
                console.error('Error starting planning:', error)
                this.createPlanningError({
                    stage: error.message || 'Error starting planning'
                })
                this.cancelLoading(false)
                return null
            }
        },
        normalizePlanningVariants(planningResponse) {
            if (!planningResponse) return []

            // Multiple planning_variants or only one variant.
            const variants = planningResponse.planning_variants
            if (Array.isArray(variants) && variants.length > 0) {
                return variants
                    .filter(v => Boolean(v?.planning_id))
                    .map(v => ({
                        planning_id: v.planning_id,
                        planning_preset: v.planning_preset ?? null
                    }))
            }

            // Single planning_id response format.
            if (planningResponse.planning_id) {
                return [{
                    planning_id: planningResponse.planning_id,
                    planning_preset: planningResponse.planning_presets?.[0] ?? null
                }]
            }

            return []
        },
        // Create the loading message for the loading Bar
        createLoadingMessage(planningProgressMap, progressResponse) {
            const allProgressValues = Object.values(planningProgressMap)
            const totalProgress = allProgressValues.reduce((sum, value) => sum + value, 0)
            const averageProgress = allProgressValues.length > 0 ? Math.round(totalProgress / allProgressValues.length) : 0
            this.loading.percent = averageProgress
            const stage =
                progressResponse.stage !== null && String(progressResponse.stage).trim() !== ''
                    ? String(progressResponse.stage).trim()
                    : ''
            this.loading.stage = `${i18n.global.t(stage)}`
        },
        async monitorPlanningProgress(planningVariant, planningProgressMap) {
            const planningId = planningVariant?.planning_id
            const planningPreset = planningVariant?.planning_preset || null

            return new Promise((resolve, reject) => {
                // We use setInterval below. Each tick can still be "in progress" when the next tick starts
                // (because we await the network). So two ticks might both think they should download the route.
                // The server only lets us download the route ONE time. So we use a flag.
                //
                // completionHandled = "we already started the one allowed download (or we already gave up)".
                // When it is true, we do nothing extra — we do not call /route again.
                //
                // Sometimes we never get a clean "100%" from /progress (error, timeout, weird response).
                // Then we still try /route once — maybe the job finished on the server anyway.
                //
                // Note: after the job is done, the server keeps the result for a limited time (~30 seconds).
                let completionHandled = false
                let isFetchingProgress = false

                // Stop polling, then GET the final route (or fail). Called when we are done waiting on /progress.
                const finalizePlanningRoute = async () => {
                    if (completionHandled) return
                    completionHandled = true
                    clearInterval(this.intervalIDs[planningId])
                    delete this.intervalIDs[planningId]
                    if (this.planningCancelled) {
                        reject({ cancelled: true })
                        return
                    }
                    const routeResult = await this.fetchPlannedRoute(planningId, planningPreset)
                    if (routeResult && !routeResult.error) {
                        resolve(routeResult)
                    } else {
                        reject(false)
                    }
                }

                this.intervalIDs[planningId] = setInterval(async () => {
                    // Avoid overlapping /progress calls (next tick while previous await is still open)
                    if (completionHandled || isFetchingProgress) return
                    isFetchingProgress = true
                    try {
                        if (this.planningCancelled) {
                            clearInterval(this.intervalIDs[planningId])
                            delete this.intervalIDs[planningId]
                            reject({ cancelled: true })
                            return
                        }

                        const progressResponse = await getPlanningProgress(planningId)

                        if (completionHandled) return

                        if (!progressResponse || progressResponse.status >= 400 || progressResponse.ok === false) {
                            if (completionHandled) return
                            // /progress did not return normal data. Before we quit, try to load the route once.
                            await finalizePlanningRoute()
                            return
                        }

                        // Store this variant's percent (0–100) for the combined progress bar.
                        planningProgressMap[planningId] = Math.max(0, Math.min(100, progressResponse.percent || 0))
                        this.createLoadingMessage(planningProgressMap, progressResponse)

                        if (progressResponse.percent === 100) {
                            await finalizePlanningRoute()
                        }
                    } catch (error) {
                        if (completionHandled) return
                        // The /progress request crashed (network, etc.). Same idea: try /route once.
                        console.error('Error fetching progress:', error)
                        await finalizePlanningRoute()
                    } finally {
                        isFetchingProgress = false
                    }
                }, 500)
            })
        },
        async fetchPlannedRoute(planningId, planningPreset = null) {
            try {
                if (this.planningCancelled) return null
                const routeResponse = await getPathPlannigRoute(planningId)

                if (!routeResponse || routeResponse.status >= 400 || routeResponse.ok === false || routeResponse.error) {
                    // A single failed variant should not cancel the whole multi-variant planning flow
                    this.createPlanningError(routeResponse)
                    return null
                }

                if (this.planningCancelled) return null

                const hasValidRoute = this.checkPlannedRoute(routeResponse)
                if (!hasValidRoute) {
                    this.createPlanningError({
                        info: 'Route geometry is missing or empty'
                    })
                    return null
                }

                return {
                    ...routeResponse,
                    planning_id: planningId,
                    planning_preset: planningPreset
                }
            } catch (error) {
                console.error('Error fetching route:', error)
                this.createPlanningError({
                    info: error.message || 'Error fetching route'
                })
                return null
            }
        },
        async planRoute(plan = null) {
            // Clear planned route error before planning a new route
            this.clearPlanningError()
            this.planningCancelled = false
            this.loading.percent = 0
            // Set updatingPlan before clearing to protect black path
            if (plan) {
                // this.updatingPlan = true
                // Transform existing rainbowPolylines to black color when entering update mode
                if (this.rainbowPolylines && this.rainbowPolylines.length > 0) {
                    this.rainbowPolylines.forEach(polyline => {
                        if (polyline) {
                            toRaw(polyline).setOptions({
                                strokeColor: '#000000',
                                strokeWeight: 10,
                                zIndex: 5
                            })
                        }
                    })
                }
            } else {
                this.updatingPlan = false
            }
            this.clearSelectionMarkers()
            this.plannedRoute = null
            this.plannedRoutes = []

            const startPoint = this.startPoint()
            const finishPoint = this.finishPoint()

            let routeDataToSend = {}

            if (plan) {
                routeDataToSend = this.updateCompletePath(plan)
            } else {
                routeDataToSend = { ...this.routeData }

                if (!startPoint.lng && !startPoint.lat) {
                    routeDataToSend.starting_point = null
                }

                if (!finishPoint.lng && !finishPoint.lat) {
                    routeDataToSend.finish_point = null
                }
            }

            // Same globals for plain planning and for reuse path / update (driven_paths_config)
            if (this.headlandTarget && !this.useAutoHeadlandTarget) {
                routeDataToSend.target_headland_index = this.headlandTarget - 1
            }

            routeDataToSend.allow_direction_change = this.allowDirectionChange
            routeDataToSend.planning_preset_configs = this.getPlanningPresetConfigs()
            routeDataToSend.fully_circle_cutouts = Boolean(this.fullyCircleCutouts)
            routeDataToSend.make_autonomous_route = Boolean(this.makeAutonomousRoute)

            // Step 1: Start planning
            const planningVariants = await this.startPlanning(routeDataToSend)
            if (!planningVariants || planningVariants.length === 0) {
                return false
            }

            // Step 2: Monitor progress with setInterval and wait to complete
            try {
                const planningProgressMap = {}
                planningVariants.forEach((variant) => {
                    planningProgressMap[variant.planning_id] = 0
                })
                const settledResults = await Promise.allSettled(
                    planningVariants.map((planningVariant) => this.monitorPlanningProgress(planningVariant, planningProgressMap))
                )

                const routeResults = settledResults
                    .filter((result) => result.status === 'fulfilled' && result.value)
                    .map((result) => result.value)

                if (routeResults.length === 0) {
                    // No variant succeeded
                    this.cancelLoading(false)
                    return false
                }

                this.plannedRoutes = routeResults
                this.currentPlannedRouteIndex = 0
                this.selectPlannedRouteByIndex(0)
                this.loading.percent = -1
                this.loading.stage = ''
                return true
            } catch (error) {
                console.error("Monitor progress error: ", error)
                return false
            }
        },
        getPlanningPresetLabel(presetValue) {
            const planningOption = this.pathPlanningOptions.find((option) => option.value === presetValue)
            if (!planningOption) {
                return presetValue
            }

            if (planningOption.variantNr) {
                return this.getVariantLabel(planningOption.variantNr, planningOption.variantName || presetValue)
            }

            return i18n.global.t(planningOption.label || presetValue)
        },
        getPlanningPresetConfigs() {
            const configs = {}
            this.pathPlanningOptions.forEach(({ value: option }) => {
                const useGlobalPoints = this.useSameStartFinishPointsByPreset[option]
                const useGlobalAllowDirectionChange = this.useSameAllowDirectionChangeByPreset[option]
                const useGlobalTurnHeadland = this.useSameTurnHeadlandByPreset[option]
                const presetStart = this.presetStartPointByPreset[option] || {}
                const presetFinish = this.presetFinishPointByPreset[option] || {}

                // Backend expects: planning_preset_configs[preset] = { config: { ... }, (optional) starting_point, finish_point }
                const presetPayload = {
                    config: {
                        corridor_error_cost: this.corridorErrorCost[option] ?? 0,
                        headland_cost_exponent: this.headlandCostExponent[option] ?? 0,
                    }
                }

                if (this.updatingPlan) {
                    presetPayload.config.missed_path_penalty = this.missedPathPenalty[option] ?? 0
                }

                // Only send per-preset points when user overrides them (checkbox OFF)
                if (!useGlobalPoints) {
                    const hasValidStart =
                        typeof presetStart?.lat === 'number' &&
                        typeof presetStart?.lng === 'number' &&
                        Number.isFinite(presetStart.lat) &&
                        Number.isFinite(presetStart.lng)
                    const hasValidFinish =
                        typeof presetFinish?.lat === 'number' &&
                        typeof presetFinish?.lng === 'number' &&
                        Number.isFinite(presetFinish.lat) &&
                        Number.isFinite(presetFinish.lng)

                    if (hasValidStart) {
                        presetPayload.starting_point = this.buildPointFeatureCollection(presetStart.lng, presetStart.lat)
                    }
                    if (hasValidFinish) {
                        presetPayload.finish_point = this.buildPointFeatureCollection(presetFinish.lng, presetFinish.lat)
                    }
                }

                if (!useGlobalAllowDirectionChange) {
                    // NOTE: 2/2 replace the next line when uncommenting the extra checkbox for allowed directions
                    // presetPayload.allow_direction_change = Boolean(this.presetAllowDirectionChangeByPreset[option])
                    presetPayload.allow_direction_change = !this.allowDirectionChange
                }

                if (!useGlobalTurnHeadland) {
                    const useAutoHeadlandTarget = this.presetUseAutoHeadlandTargetByPreset[option]
                    const presetHeadlandTarget = this.presetHeadlandTargetByPreset[option]
                    const backendHeadlandIndex = this.presetBackendHeadlandIndexByPreset[option]
                    if (useAutoHeadlandTarget === false && typeof presetHeadlandTarget === 'number' && Number.isFinite(presetHeadlandTarget)) {
                        presetPayload.target_headland_index = presetHeadlandTarget - 1
                    } else if (
                        typeof backendHeadlandIndex === 'number' &&
                        Number.isFinite(backendHeadlandIndex) &&
                        backendHeadlandIndex >= 0
                    ) {
                        presetPayload.target_headland_index = backendHeadlandIndex
                    }
                }

                configs[option] = presetPayload
            })
            return configs
        },
        async loadPlanningPresetConfigsByTaskType(taskType) {
            if (!taskType) {
                return
            }

            this.pathPlanningOptions = []

            try {
                const response = await getPlanningPresetsByTaskType(taskType)
                const backendConfigs = response?.planning_preset_configs
                if (!backendConfigs || typeof backendConfigs !== 'object') {
                    return
                }

                const backendOptions = Object.entries(backendConfigs).map(([presetKey, presetConfig]) => {
                    const variantName = presetConfig?.variant_name || presetKey
                    const variantMatch = String(presetKey).match(/^variant_(\d+)$/i) // variant_1, variant_2, etc.
                    return {
                        value: presetKey,
                        label: variantName,
                        variantName,
                        variantNr: variantMatch ? parseInt(variantMatch[1], 10) : null
                    }
                })

                if (backendOptions.length > 0) {
                    this.pathPlanningOptions = backendOptions
                }

                this.presetBackendHeadlandIndexByPreset = {}

                Object.entries(backendConfigs).forEach(([presetKey, presetConfig]) => {
                    const cfg = presetConfig?.config

                    this.corridorErrorCost[presetKey] = cfg?.corridor_error_cost ?? 0
                    this.headlandCostExponent[presetKey] = cfg?.headland_cost_exponent ?? 0
                    this.missedPathPenalty[presetKey] = cfg?.missed_path_penalty ?? 0

                    this.initPresetDefaults(presetKey, taskType)

                    const headlandIdx = Number(presetConfig?.target_headland_index)
                    if (Number.isFinite(headlandIdx) && headlandIdx >= 0) {
                        this.presetBackendHeadlandIndexByPreset[presetKey] = headlandIdx
                        this.presetHeadlandTargetByPreset[presetKey] = headlandIdx + 1
                    }
                })
                this.setGlobalAllowDirectionChange(taskType)
            } catch (error) {
                console.error('Error fetching planning preset configs:', error)
            }
        },
        setGlobalAllowDirectionChange(taskType) {
            if (!taskType) return

            // For all spraying tasks, allow direction change by default
            const allowDirectionChangeSprayingTasks = ['spraying_42m', 'spraying_56m', 'spraying_70m']
            if (allowDirectionChangeSprayingTasks.includes(taskType)) {
                this.allowDirectionChange = true
            } else {
                this.allowDirectionChange = false
            }
        },
        selectPlannedRouteByIndex(index) {
            if (!Array.isArray(this.plannedRoutes) || this.plannedRoutes.length === 0) {
                this.plannedRoute = null
                this.currentPlannedRouteIndex = 0
                return null
            }

            if (index < 0 || index >= this.plannedRoutes.length) {
                return null
            }

            // Clear currently displayed route from map only, without removing stored variants.
            this.clearMarkers(false, false, this.updatingPlan)
            this.plannedRoute = this.plannedRoutes[index]
            this.currentPlannedRouteIndex = index

            if (this.plannedRoute) {
                this.showPathRoute(this.plannedRoute)
                this.showUncoveredPolygons(this.plannedRoute, false)
                this.showPresetMarkersForOption(this.plannedRoute.planning_preset)
                if (this.uncoveredZonesPulsing) {
                    this.startUncoveredPulsing()
                }
            }
            return this.plannedRoute
        },
        createPlanningError(response) {
            if (!response) {
                this.planningError = 'Unknown planning error'
                this.retrySeconds = null
                return
            }
            if (response.error) {
                this.planningError = response.error
                this.retrySeconds = null
            } else {
                const data = response.data || response
                const parsedRetry = Number(data.retry_after_seconds)
                this.retrySeconds = Number.isFinite(parsedRetry) ? parsedRetry : null
                this.planningError = data.stage || data.info || data.problem || data.reason || 'Unknown planning error'
            }
        },
        getPlanningError() {
            let error = ''
            if (this.planningError) {
                error = i18n.global.t(this.planningError)
            } else {
                error = i18n.global.t('Unknown planning error')
            }
            this.clearPlanningError()
            return error
        },
        clearPlanningError() {
            this.planningError = ''
            this.retrySeconds = null
        },
        checkPlannedRoute(routeResponse) {
            switch (true) {
                case !routeResponse:
                    return false
                case !routeResponse.geometry:
                    return false
                case !routeResponse.geometry.coordinates:
                    return false
                case routeResponse.geometry.coordinates.length === 0:
                    return false
                default:
                    return true
            }
        },
        updateCompletePath(plan) {
            const ts = useTasksStore()
            // from Select dropdown
            const selectedTaskType = this.routeData.task_type
            // if selectedTaskType is not set, meaning we are updating a field plan
            const taskTypeToUse = selectedTaskType || plan.type
            this.updatingPlan = true
            this.clearUpdatePathPolylines()
            // set selected plan only if we are updating a field plan
            if (!selectedTaskType) {
                this.selectedPlan = plan
                this.planName = plan.name
            }

            this.routeData.task_type = taskTypeToUse
            const startingPoint = plan.geometry.coordinates[0][0]
            const lastLineString = plan.geometry.coordinates[plan.geometry.coordinates.length - 1]
            const finishPoint = lastLineString[lastLineString.length - 1]

            return {
                field_id: ts.field.id,
                task_type: taskTypeToUse,
                starting_point: {
                    type: "FeatureCollection",
                    features: [
                        {
                            type: "Feature",
                            properties: {},
                            geometry: {
                                type: "Point",
                                // first coordonate from array
                                coordinates: [startingPoint[0], startingPoint[1]]
                            }
                        }
                    ]
                },
                finish_point: {
                    type: "FeatureCollection",
                    features: [
                        {
                            type: "Feature",
                            properties: {},
                            geometry: {
                                type: "Point",
                                // last coordonate from array
                                coordinates: [finishPoint[0], finishPoint[1]]
                            }
                        }
                    ]
                },
                driven_paths_config: {
                    path_ids: [plan.id]
                }
            }
        },
        showPathRoute(res) {
            if (res && res.segmented_geometry) {
                const geoJsonData = {
                    type: 'FeatureCollection',
                    features: [
                        {
                            type: 'Feature',
                            geometry: res.segmented_geometry,
                        }
                    ]
                }
                this.drawPath(geoJsonData)
            }
        },
        async savePathRoute(taskId, planId = null) {
            const tasksStore = useTasksStore()
            const tfs = useTasksFilterStore()

            if (taskId == null || taskId === '') {
                return false
            }

            const data = {
                task_id: taskId,
                name: this.planName,
                geometry: {
                    type: 'MultiLineString',
                    coordinates: this.plannedRoute.segmented_geometry.coordinates,
                },
                generator_version: this.plannedRoute.generator_version,
                working_width: this.plannedRoute.working_width,
                type: this.routeData.task_type
            }

            if (planId) {
                data.id = planId
            }

            try {
                this.loadingSave = true
                this.saveError = null
                const response = await createPlan(data)
                if (!response || response.ok === false) {
                    this.saveError = response?.data?.reason ?? null
                    throw new Error('Error creating plan')
                }
                await tasksStore.getTaskPlans(taskId, this.routeData.task_type)
                await tasksStore.getFieldTasks(tasksStore.field.id)

                this.clearRainbowPolylines()
                this.clearUpdatePathPolylines()
                this.clearPlanningError()

                // Disable the old path if we are updating. It will be a new path id generated.
                if (this.updatingPlan) {
                    await tasksStore.disablePath(this.selectedPlan.id)
                    this.updatingPlan = false
                }

                // Update path statistic after disabling the old path
                await tfs.getFieldPathStatistic()

                this.selectedPlan = response
                this.loadingSave = false
                return true
            } catch (error) {
                console.error('Error saving plan:', error)
                this.loadingSave = false
                this.selectPlannedRouteByIndex(this.currentPlannedRouteIndex)
                return false
            }
        },
        clearSelectionMarkers() {
            this.selectionMarkers.forEach((marker) => {
                marker.map = null
            })
            this.selectionMarkers = []
        },
        addSelectionMarkers() {
            const tasksStore = useTasksStore()

            this.clearSelectionMarkers()
            const field = tasksStore.field
            if (field && field.ab_lines) {
                field.ab_lines.coordinates.forEach((line, lineIndex) => {
                    const startPosition = { lat: line[0][1], lng: line[0][0] }
                    const endPosition = { lat: line[line.length - 1][1], lng: line[line.length - 1][0] }

                    const startMarker = this.createMarker(startPosition, (lineIndex + 1).toString() + '.a', 1, '#0000FF', 'Start')
                    const endMarker = this.createMarker(endPosition, (lineIndex + 1).toString() + '.b', 1, '#0000FF', 'Finish')

                    this.selectionMarkers.push(startMarker)
                    this.selectionMarkers.push(endMarker)
                })
            }
            // When blue AB markers are re-added, restore preset markers that have custom coordinates
        },
        getMarkerTitle() {
            const startPoint = this.startPoint()

            if (!startPoint.lng && !startPoint.lat) {
                return 'Starting Point'
            }
            return 'Finish Point'
        },
        clearPaths() {
            const mapStore = useMaps()

            mapStore.map.data.forEach(feature => {
                try {
                    if (feature && feature.getGeometry() && feature.getGeometry().getType() === 'MultiLineString') {
                        mapStore.map.data.remove(feature)
                    }
                } catch (e) {
                    // If there's an error, remove the feature anyway
                    console.error("Error on clearPaths: ", e)
                    if (feature) {
                        mapStore.map.data.remove(feature)
                    }
                }
            })
        },
        createRainbowPolyline(coordinates, color = null, zIndex = 6, strokeWeight = 5) {
            const mapStore = useMaps()
            const rainbowPolylines = []

            const segmentLength = Math.round(coordinates.length / rainbowColors.length + 1)
            for (let i = 0; i < rainbowColors.length; i++) {
                const segmentCoordinates = coordinates.slice(i * segmentLength, (i + 1) * segmentLength + 1)
                if (segmentCoordinates.length > 1) {
                    const polyline = new mapStore.google.maps.Polyline({
                        path: segmentCoordinates,
                        strokeColor: color ? color : rainbowColors[i],
                        strokeWeight,
                        strokeOpacity: 1,
                        zIndex: zIndex,
                        map: toRaw(mapStore.map),
                        clickable: false
                    })

                    // Store original color index for later restoration
                    polyline.originalColorIndex = i
                    rainbowPolylines.push(polyline)
                }
            }

            return rainbowPolylines
        },
        restoreRainbowColors() {
            // Restore rainbow colors to rainbowPolylines
            if (this.rainbowPolylines && this.rainbowPolylines.length > 0) {
                this.rainbowPolylines.forEach((polyline, index) => {
                    if (polyline) {
                        const colorIndex = polyline.originalColorIndex !== undefined
                            ? polyline.originalColorIndex
                            : index % rainbowColors.length
                        toRaw(polyline).setOptions({
                            strokeColor: rainbowColors[colorIndex],
                            zIndex: 6
                        })
                    }
                })
            }
        },
        addDirectionalArrows(rainbowPolylines, fillColor, strokeColor) {
            const mapStore = useMaps()

            if (rainbowPolylines.length > 0) {
                const lineSymbol = {
                    path: mapStore.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                    fillColor: fillColor,
                    fillOpacity: 1,
                    strokeColor: strokeColor,
                    strokeWeight: 1,
                    scale: 3
                }

                rainbowPolylines.forEach(polyline => {
                    polyline.setOptions({
                        icons: [{
                            icon: lineSymbol,
                            offset: '0%',
                            repeat: '100px'
                        }],
                        clickable: false
                    })
                })
            }
        },
        async getTaskTypes() {
            if (this.taskTypes.length > 0) {
                return this.taskTypes
            }

            try {
                const res = await getPathPlanningOptions()
                this.taskTypes = res
                return this.taskTypes
            } catch (error) {
                console.error('Error fetching task types:', error)
                return []
            }
        },
        processPolygonRings(polygonRings) {
            if (polygonRings.length === 0) return null

            // First ring is outer boundary
            const outerRing = polygonRings[0]
            if (!outerRing || !Array.isArray(outerRing)) {
                return null
            }

            // Take the outer ring coordinates
            const outerPath = outerRing.map(coord => ({
                lat: coord[1],
                lng: coord[0]
            }))

            if (outerPath.length === 0) return null

            const paths = [outerPath] // First path is outer ring

            // Process inner rings (holes) if they exist
            if (polygonRings.length > 1) {
                for (let i = 1; i < polygonRings.length; i++) {
                    const innerRing = polygonRings[i]
                    if (innerRing && Array.isArray(innerRing)) {
                        const innerPath = innerRing.map(coord => ({
                            lat: coord[1],
                            lng: coord[0]
                        }))

                        if (innerPath.length > 0) {
                            paths.push(innerPath) // Add as inner ring (hole) to outer ring
                        }
                    }
                }
            }

            return paths
        },
        showUncoveredPolygons(res, clickableBool = true) {
            const mapStore = useMaps()

            this.clearUncoveredPolygons()

            const uncoveredAreaGeometry = res.uncovered_area_geometry
            if (!uncoveredAreaGeometry) return

            uncoveredAreaGeometry.coordinates.forEach(polygonRings => {
                const paths = this.processPolygonRings(polygonRings)
                if (!paths) return

                const polygon = new mapStore.google.maps.Polygon({
                    paths: paths,
                    strokeColor: '#FF0000',
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    fillColor: '#FF0000',
                    fillOpacity: 0.5,
                    map: mapStore.map,
                    zIndex: 1000,
                    clickable: clickableBool
                })

                toRaw(this.uncoveredPolygons).push(polygon)
            })
        },
        stopUncoveredPulsing() {
            if (this.uncoveredPulsingInterval) {
                clearInterval(this.uncoveredPulsingInterval)
                this.uncoveredPulsingInterval = null
            }
            this.uncoveredPolygons.forEach((p) => {
                toRaw(p).setOptions({ strokeWeight: 2, zIndex: 1000 })
            })
        },
        startUncoveredPulsing() {
            this.stopUncoveredPulsing()
            if (!this.uncoveredPolygons.length && this.plannedRoute?.uncovered_area_geometry?.coordinates?.length) {
                this.showUncoveredPolygons(this.plannedRoute)
            }
            if (!this.uncoveredPolygons.length) {
                this.uncoveredZonesPulsing = false
                return
            }
            let weight = 2, isGrowing = true
            this.uncoveredPulsingInterval = setInterval(() => {
                weight += isGrowing ? 1 : -1
                if (weight >= 6) isGrowing = false
                if (weight <= 2) isGrowing = true
                this.uncoveredPolygons.forEach((p) => toRaw(p).setOptions({ strokeWeight: weight, zIndex: 1000 }))
            }, 100)
        },
        isNexatAdminUser() {
            const ur = useUserRole()
            const ts = useTasksStore()

            const selectedCustomer = ts.field.farm_id
            return ur.isUserNexatAdminForOrgId(selectedCustomer)
        },
        getIcon(option) {
            const planningOption = this.pathPlanningOptions.find((opt) => opt.value === option)
            const source = planningOption?.variantName || planningOption?.label || option
            const label = String(source || '').toLowerCase()
            if (label.includes('distance')) {
                return 'fa-solid fa-route'
            }
            if (label.includes('curve')) {
                return 'fa-solid fa-bezier-curve'
            }
            if (label.includes('compaction')) {
                return 'fa-solid fa-tractor'
            }
            return 'fa-solid fa-sliders'
        },
        getVariantLabel(variantNr, variantName) {
            /**
             * Variant name is comming from the backend but,
             * does not make sense to show it to the user FOR NOW
             * because not reflecting the actual variant name
             */
            // return `${i18n.global.t('Variant')} ${variantNr} - ${i18n.global.t(variantName)}`
            return `${i18n.global.t('Variant')} ${variantNr}`
        },
        tooltipTextAnotherPointPicking() {
            if (!this.presetPointPicking.option || !this.presetPointPicking.point) {
                return ''
            }

            const pickingOption = this.presetPointPicking.option
            const labelOption = this.getPlanningPresetLabel(pickingOption)
            const pickingPoint = this.presetPointPicking.point

            return i18n.global.t('Another point is waiting to be picked on the map: ') + labelOption + ' ' + i18n.global.t(pickingPoint)
        },
        async loadFieldPaths() {
            const ts = useTasksStore()
            const fieldPlans = await getFieldPlans(ts.field.id)
            if (!Array.isArray(fieldPlans)) {
                this.fieldPlans = []
                return this.fieldPlans
            }

            this.fieldPlans = fieldPlans.map((plan) => ({
                id: plan?.id ?? null,
                name: plan?.name ?? ''
            }))

            return this.fieldPlans
        }
    },
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(usePlanner, import.meta.hot))
}
